import { Link, useLocation } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { api, type Portfolio, type Project } from './api.js'
import { t, t as tr } from './i18n.js'

/** Breadcrumb row under the top bar, derived from the route (project names resolved from cache). */
export function Breadcrumbs() {
  const { pathname } = useLocation()
  const projects = useQuery({ queryKey: ['projects', true], queryFn: () => api<{ projects: Project[] }>('/projects?includeArchived=true'), staleTime: 60_000, enabled: pathname.startsWith('/projects/') })
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios'), staleTime: 60_000, enabled: pathname.startsWith('/portfolios/') })
  const parts = pathname.split('/').filter(Boolean)
  const crumbs: { label: string; to?: string }[] = [{ label: t('nav.home'), to: '/' }]
  const managed: Record<string, string> = { timesheets: t('nav.timesheets'), leave: t('nav.leave'), reports: t('nav.reports'), payroll: t('nav.payroll') }
  const tabs: Record<string, string> = { board: 'Board', list: 'List', gantt: 'Gantt', calendar: 'Calendar', analytics: 'Analytics', approvals: 'Approvals', rates: 'Rates & budget' }
  if (parts[0] === 'projects') {
    crumbs.push({ label: t('nav.projects'), to: '/projects' })
    if (parts[1] === 'new') crumbs.push({ label: 'New project' })
    else if (parts[1]) {
      const p = projects.data?.projects.find((x) => x.id === parts[1])
      crumbs.push({ label: p ? `${p.code} ${p.name}` : '…', to: `/projects/${parts[1]}` })
      if (parts[2] && tabs[parts[2]]) crumbs.push({ label: tabs[parts[2]]! })
      else if (!parts[2]) crumbs.push({ label: 'Overview' })
    }
  } else if (parts[0] === 'portfolios') {
    crumbs.push({ label: t('nav.portfolios'), to: '/portfolios' })
    if (parts[1]) {
      const p = portfolios.data?.portfolios.find((x) => x.id === parts[1])
      crumbs.push({ label: p ? p.name : '…', to: `/portfolios/${parts[1]}` })
      if (parts[2] && tabs[parts[2]]) crumbs.push({ label: tabs[parts[2]]! })
      else if (!parts[2]) crumbs.push({ label: 'Overview' })
    }
  } else if (parts[0] && managed[parts[0]]) { crumbs.push({ label: 'Management' }); crumbs.push({ label: managed[parts[0]]! }) }
  else if (parts[0] === 'people') crumbs.push({ label: t('nav.people') })
  else if (parts[0] === 'time') crumbs.push({ label: t('nav.time') })
  else if (parts[0] === 'account') crumbs.push({ label: 'Account' })
  else if (parts[0] === 'search') crumbs.push({ label: 'Search' })
  else if (parts[0] === 'admin') { crumbs.push({ label: t('nav.admin'), to: '/admin' }); if (parts[1] === 'import') crumbs.push({ label: 'Import' }) }
  return (
    <nav className="crumbbar" aria-label={tr("Breadcrumb")}>
      {crumbs.map((c, i) => <span key={i} className="crumb">{i > 0 && <ChevronRight size={12} aria-hidden className="muted" />}{c.to && i < crumbs.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span aria-current={i === crumbs.length - 1 ? 'page' : undefined}>{c.label}</span>}</span>)}
    </nav>
  )
}
